import { z } from 'zod';

/**
 * SIL-1071: `buyer_cost_constant` wire DTOs — the org's house rates for the
 * Market Intelligence purchase-decision section (migration 893). The engine
 * (a future purchase-engine.ts, SIL-1072) keys strictly off `kind`, never
 * `key`/`label` — this schema is the source of truth for that enum, and the
 * DB CHECK on `buyer_cost_constant.kind` must be kept in sync by hand (no
 * generated-from-zod constraint exists yet).
 *
 * Percent kinds (`pct_of_revenue`, `pct_of_acquisition_cost`) are FRACTIONS:
 * 0.15 means 15%. This is the same convention `target_margin_pct` uses on
 * `purchase_analysis` (SIL-1072) — one convention, named consistently.
 */
export const BUYER_COST_CONSTANT_KINDS = [
  'per_unit',
  'per_unit_month',
  'pct_of_revenue',
  'pct_of_acquisition_cost',
  'fixed_per_analysis',
] as const;

export const buyerCostConstantKindSchema = z.enum(BUYER_COST_CONSTANT_KINDS);
export type BuyerCostConstantKind = z.infer<typeof buyerCostConstantKindSchema>;

const PERCENT_KINDS = new Set<BuyerCostConstantKind>(['pct_of_revenue', 'pct_of_acquisition_cost']);

/**
 * The seven standard keys seeded for every org by migration 894. Reserved
 * only for UI labeling/ordering — a user-defined key is just another row
 * with a `kind`, so this list is NOT a closed set / CHECK constraint.
 */
export const STANDARD_BUYER_COST_CONSTANT_KEYS = [
  'storage_usd_per_unit_month',
  'inbound_freight_usd_per_unit',
  'outbound_freight_usd_per_unit',
  'labor_usd_per_unit',
  'refurb_parts_usd_per_unit',
  'marketplace_fee',
  'import_duty',
] as const;

export const buyerCostConstantRowSchema = z.object({
  customerOrgId: z.string().uuid(),
  key: z.string().min(1).max(100),
  label: z.string().min(1).max(200),
  kind: buyerCostConstantKindSchema,
  value: z.number().finite(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BuyerCostConstantRow = z.infer<typeof buyerCostConstantRowSchema>;

export const buyerCostConstantListResponseSchema = z.object({
  rows: z.array(buyerCostConstantRowSchema),
  /**
   * false when no row for the org has a non-zero value. Migration 894 seeds
   * every org at 0, so a row count alone can never distinguish "seeded and
   * untouched" from "configured" — this is the signal the UI's first-run
   * gate and analysis-level warnings key on.
   */
  costsConfigured: z.boolean(),
});
export type BuyerCostConstantListResponse = z.infer<typeof buyerCostConstantListResponseSchema>;

/** One row of the `PUT /api/client/buyer-cost-constants` whole-replace body. */
export const buyerCostConstantWriteRowSchema = z
  .object({
    key: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    kind: buyerCostConstantKindSchema,
    value: z.number().finite().min(0),
  })
  .refine((row) => !PERCENT_KINDS.has(row.kind) || row.value <= 1, {
    message: 'Percent-kind constants (pct_of_revenue, pct_of_acquisition_cost) must be <= 1 (a fraction, e.g. 0.15 for 15%).',
    path: ['value'],
  });
export type BuyerCostConstantWriteRow = z.infer<typeof buyerCostConstantWriteRowSchema>;

/**
 * Whole-replace body: every key present is upserted, every key omitted is
 * deleted (scenarios.ts `PUT /:id` whole-replace precedent). Duplicate keys
 * in one body are a client bug, not a database concern — reject before any
 * write.
 */
export const buyerCostConstantWriteRequestSchema = z.object({
  rows: z.array(buyerCostConstantWriteRowSchema),
});
export type BuyerCostConstantWriteRequest = z.infer<typeof buyerCostConstantWriteRequestSchema>;
